import type { ECharts } from './echarts-types.ts';
import type { ChartConfig } from './types.ts';
import { isDark } from './theme.ts';

/**
 * Derive a slugified filename from the chart's title (or a timestamp fallback).
 */
export function exportFilename(raw: string): string {
  try {
    const cfg = JSON.parse(raw) as ChartConfig;
    if (cfg.title) {
      return cfg.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }
  } catch {
    /* fall through */
  }
  return `chart-${Date.now()}`;
}

/**
 * Resolve the computed background colour of the nearest opaque ancestor so
 * the exported image has a proper background instead of transparency.
 */
export function resolveExportBg(el: HTMLElement): string {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const bg = getComputedStyle(node).backgroundColor;
    // Skip transparent / rgba(0,0,0,0) entries
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg;
    node = node.parentElement;
  }
  return isDark() ? '#1a1a1a' : '#ffffff';
}

/** Download an SVG string as a .svg file. */
export function downloadSvg(svgString: string, filename: string): void {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.svg`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Render the ECharts SVG onto a canvas, inject a background, download as PNG. */
export function downloadPng(
  svgEl: SVGElement,
  bg: string,
  filename: string
): void {
  const { width, height } = svgEl.getBoundingClientRect();
  const scale = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(scale, scale);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  const svgData = new XMLSerializer().serializeToString(svgEl);
  const img = new Image();
  const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  img.onload = () => {
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${filename}.png`;
    a.click();
  };
  img.src = url;
}

export function downloadChartRaster(
  chart: ECharts,
  bg: string,
  filename: string
): void {
  const url = chart.getDataURL({
    type: 'png',
    pixelRatio: 2,
    backgroundColor: bg,
  });
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.png`;
  a.click();
}

/**
 * Inject a small "Download" button overlay into the wrapper element.
 * The button is hidden until the wrapper is hovered.
 */
export function attachExportButton(
  wrapper: HTMLElement,
  chart: ECharts,
  raw: string
): void {
  // Wrapper must be relatively positioned for absolute child placement.
  wrapper.style.position = 'relative';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Download chart');
  btn.style.cssText = [
    'position:absolute',
    'top:6px',
    'right:6px',
    'z-index:10',
    'display:flex',
    'align-items:center',
    'gap:4px',
    'padding:3px 8px',
    'border-radius:5px',
    'border:1px solid rgba(128,128,128,0.25)',
    'background:rgba(0,0,0,0.45)',
    'color:rgba(255,255,255,0.85)',
    'font-size:11px',
    'font-family:inherit',
    'line-height:1.4',
    'cursor:pointer',
    'opacity:0.4',
    'transition:opacity 0.15s',
    'backdrop-filter:blur(4px)',
    '-webkit-backdrop-filter:blur(4px)',
    'pointer-events:auto',
  ].join(';');
  btn.innerHTML =
    `<svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
    `<path d="M8 2v9M4 8l4 4 4-4"/><path d="M2 13h12"/>` +
    `</svg>Download`;

  // Subdued by default; full opacity on the control only (no wrapper hover — avoids fighting ECharts hit-testing).
  btn.addEventListener('mouseenter', () => {
    btn.style.opacity = '1';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.opacity = '0.4';
  });

  let menu: HTMLElement | null = null;

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu) {
      menu.remove();
      menu = null;
      return;
    }

    const filename = exportFilename(raw);
    const bg = resolveExportBg(wrapper);

    // Get the SVG element rendered by ECharts inside the wrapper.
    const svgEl = wrapper.querySelector('svg') as SVGElement | null;

    menu = document.createElement('div');
    menu.style.cssText = [
      'position:absolute',
      'top:28px',
      'right:6px',
      'z-index:20',
      'border-radius:6px',
      'border:1px solid rgba(128,128,128,0.25)',
      'background:rgba(24,24,24,0.95)',
      'backdrop-filter:blur(6px)',
      '-webkit-backdrop-filter:blur(6px)',
      'overflow:hidden',
      'min-width:110px',
    ].join(';');

    const itemStyle = [
      'display:block',
      'width:100%',
      'padding:7px 12px',
      'text-align:left',
      'background:none',
      'border:none',
      'color:rgba(255,255,255,0.85)',
      'font-size:11px',
      'font-family:inherit',
      'cursor:pointer',
      'white-space:nowrap',
    ].join(';');

    const svgItem = document.createElement('button');
    svgItem.type = 'button';
    svgItem.style.cssText = itemStyle;
    svgItem.textContent = 'Download SVG';
    svgItem.addEventListener('mouseenter', () => {
      svgItem.style.background = 'rgba(255,255,255,0.08)';
    });
    svgItem.addEventListener('mouseleave', () => {
      svgItem.style.background = 'none';
    });
    svgItem.addEventListener('click', () => {
      if (svgEl) {
        // Clone so we can inject a background without affecting the live chart.
        const clone = svgEl.cloneNode(true) as SVGElement;
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        // Insert a background rect as the first child.
        const rect = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect'
        );
        rect.setAttribute('width', '100%');
        rect.setAttribute('height', '100%');
        rect.setAttribute('fill', bg);
        clone.insertBefore(rect, clone.firstChild);
        downloadSvg(new XMLSerializer().serializeToString(clone), filename);
      } else {
        downloadChartRaster(chart, bg, filename);
      }
      menu?.remove();
      menu = null;
    });

    const pngItem = document.createElement('button');
    pngItem.type = 'button';
    pngItem.style.cssText = itemStyle;
    pngItem.textContent = 'Download PNG';
    pngItem.addEventListener('mouseenter', () => {
      pngItem.style.background = 'rgba(255,255,255,0.08)';
    });
    pngItem.addEventListener('mouseleave', () => {
      pngItem.style.background = 'none';
    });
    pngItem.addEventListener('click', () => {
      if (svgEl) {
        downloadPng(svgEl, bg, filename);
      } else {
        downloadChartRaster(chart, bg, filename);
      }
      menu?.remove();
      menu = null;
    });

    menu.appendChild(svgItem);
    menu.appendChild(pngItem);
    wrapper.appendChild(menu);

    // Dismiss on outside click
    const dismiss = (ev: MouseEvent) => {
      if (!menu?.contains(ev.target as Node)) {
        menu?.remove();
        menu = null;
        document.removeEventListener('click', dismiss);
      }
    };
    setTimeout(() => {
      document.addEventListener('click', dismiss);
    }, 0);
  });

  wrapper.appendChild(btn);
}
